import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LIBRARY_PATH } from '@/constants/appRoutes';
import { useAuth } from '@/contexts/AuthContext';
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  libraryQuestionsApi,
  type LibraryQuestionDetail,
} from '@/services/api';
import { AnswerVote, byAnswerRank, CommunityPostHeading, RightAnswerControl } from '@/features/chat/components/CommunityPost';

export function LibraryQuestionPage() {
  const { id } = useParams<{ id: string }>();
  const { isAdmin } = useAuth();
  const { setBreadcrumbLabel } = useBreadcrumbLabel();
  const [question, setQuestion] = useState<LibraryQuestionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const [votingId, setVotingId] = useState<string | null>(null);
  const [markingId, setMarkingId] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    libraryQuestionsApi
      .get(id)
      .then((data) => {
        if (cancelled) return;
        setQuestion(data);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this question.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const label = question?.body.trim().slice(0, 80) || null;
    setBreadcrumbLabel(label);
    return () => setBreadcrumbLabel(null);
  }, [question?.body, setBreadcrumbLabel]);

  const canAnswer = question != null && (question.channel === 'community' || isAdmin);

  const vote = (answerId: string, direction: 1 | -1) => {
    if (!question || votingId) return;
    setVotingId(answerId);
    void libraryQuestionsApi
      .voteAnswer(question.id, answerId, direction)
      .then((updated) => {
        setQuestion((current) => {
          if (!current) return current;
          return {
            ...current,
            answers: byAnswerRank(
              current.answers.map((answer) =>
                answer.id === updated.id ? { ...answer, score: updated.score, my_vote: updated.my_vote } : answer,
              ),
            ),
          };
        });
      })
      .finally(() => setVotingId(null));
  };

  const mark = (answerId: string) => {
    if (!question || markingId) return;
    setMarkingId(answerId);
    void libraryQuestionsApi
      .acceptAnswer(question.id, answerId)
      .then((updated) => setQuestion(updated))
      .finally(() => setMarkingId(null));
  };

  const submitAnswer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !draft.trim() || saving) return;
    setSaving(true);
    setAnswerError(null);
    try {
      const updated = await libraryQuestionsApi.answer(id, draft.trim());
      setQuestion(updated);
      setDraft('');
    } catch {
      setAnswerError('Could not save that answer.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="flex-1 overflow-y-auto px-5 py-10 text-center">
        <p className="text-sm text-destructive">{error ?? 'Question not found.'}</p>
        <Link to={LIBRARY_PATH} className="mt-3 inline-block text-sm font-medium text-primary hover:underline">
          Back to questions
        </Link>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto container-scroll">
      <div className="mx-auto max-w-3xl px-5 py-5 md:px-8 md:py-7">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="rounded-full bg-muted px-2 py-0.5 font-medium capitalize text-foreground">
            {question.channel}
          </span>
          <span className="capitalize">{question.status}</span>
          {question.author_name && <span>{question.author_name}</span>}
          <span>{new Date(question.created_at).toLocaleString()}</span>
        </div>
        <h1 className="mb-8 whitespace-pre-wrap text-xl font-semibold leading-relaxed text-foreground">
          {question.body}
        </h1>

        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {question.answers.length === 0 ? 'No answers yet' : 'Answers'}
        </h2>
        <ul className="space-y-3">
          {question.answers.map((answer) => (
            <li key={answer.id} className="flex gap-2 rounded-xl border border-border bg-card px-4 py-3">
              {question.channel === 'community' && (
                <AnswerVote
                  score={answer.score}
                  myVote={answer.my_vote}
                  disabled={votingId === answer.id}
                  onVote={(direction) => vote(answer.id, direction)}
                />
              )}
              <div className="min-w-0 flex-1">
                <CommunityPostHeading
                  authorName={answer.author_name}
                  createdAt={answer.created_at}
                  meta={
                    <RightAnswerControl
                      accepted={answer.is_accepted}
                      canMark={isAdmin}
                      disabled={markingId === answer.id}
                      onToggle={() => mark(answer.id)}
                    />
                  }
                />
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{answer.body}</p>
              </div>
            </li>
          ))}
        </ul>

        {canAnswer ? (
          <form onSubmit={submitAnswer} className="mt-6 space-y-3">
            <label htmlFor="library-answer" className="text-sm font-medium text-foreground">
              {question.channel === 'staff' ? 'Staff answer' : 'Your answer'}
            </label>
            <Textarea
              id="library-answer"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={5}
              placeholder="Write an answer…"
            />
            {answerError && <p className="text-sm text-destructive">{answerError}</p>}
            <Button type="submit" disabled={saving || !draft.trim()}>
              {saving ? 'Saving…' : 'Post answer'}
            </Button>
          </form>
        ) : (
          <p className="mt-6 text-sm text-muted-foreground">
            Only the RCM team can answer staff questions.
          </p>
        )}
      </div>
    </div>
  );
}
